import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/authService';
import TechnicianOrdersView from './TechnicianOrdersView';
import TechnicianCommissionsView from './TechnicianCommissionsView';
import TechnicianDashboardView from './TechnicianDashboardView';
import TechnicianContratosView from './TechnicianContratosView';
import zapataLogo from '../../assets/zapata-logo.png';
import { getMessagingInstance, getToken, FCM_VAPID_KEY } from '../../config/firebase';
import { Icons } from '../../utils/icons';
import { useOfflineQueue } from '../../hooks/useOfflineQueue';
import { capturePageview } from '../../lib/analytics';

const TABS = [
  { id: 'orders',      label: 'Órdenes',    icon: '📋' },
  { id: 'commissions', label: 'Comisiones', icon: '💶' },
  { id: 'contratos',   label: 'Contratos',   icon: '📄' },
  { id: 'profile',     label: 'Mi Perfil',  icon: '👤' },
];

function getInitials(name = '') {
  return name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();
}

async function registerPushToken(user) {
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return;

    const messaging = await getMessagingInstance();
    if (!messaging) return;

    const token = await getToken(messaging, {
      vapidKey: FCM_VAPID_KEY,
      serviceWorkerRegistration: await navigator.serviceWorker.register('/firebase-messaging-sw.js'),
    });

    if (token && user?.id) {
      await fetch('/.netlify/functions/saveFcmToken', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ token, technicianId: user.id, technicianName: user.name || '', employeeId: user.employeeId }),
      });
    }
  } catch { /* notificaciones no disponibles en este dispositivo */ }
}

export default function TechnicianApp() {
  const { user, logout, isAdminImpersonating, exitTechnicianMode, updateUser } = useAuth();
  const [activeTab, setActiveTab] = useState('orders');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const { pending, failed, flushing, flushNow } = useOfflineQueue();

  useEffect(() => {
    // "Órdenes" manda su propio pageview, más granular (lista/detalle),
    // desde TechnicianOrdersView — mandarlo aquí también duplicaba el
    // evento en el mismo pantallazo (Rafa, 2026-11-04).
    if (activeTab === 'orders') return;
    capturePageview(`tecnico:${activeTab}`);
  }, [activeTab]);

  // Solo para el admin probando como técnico. Antes este botón solo subía
  // (Firebase → StelOrder): las acciones de campo ya no escriben en
  // StelOrder al momento, quedan en cola (stelSyncEstado:'pendiente') y las
  // sube el cron syncStelOrderPendientes cada 5 min — este botón disparaba
  // esa subida ya. Pero el sentido contrario (StelOrder → Firebase, nuevas
  // asignaciones u órdenes modificadas desde StelOrder) solo corría cada 30
  // min vía syncDeltaStelOrder, así que una orden recién asignada en
  // StelOrder no aparecía en la app aunque se pulsara este botón — confusión
  // real detectada probando como admin (2026-10-14). Ahora hace las dos
  // cosas: sube primero lo pendiente del técnico, baja después lo nuevo de
  // StelOrder, y al terminar fuerza recargar la lista de órdenes.
  const [syncingStelOrder, setSyncingStelOrder] = useState(false);
  const [syncResult,       setSyncResult]       = useState(null);
  const [refreshTrigger,   setRefreshTrigger]   = useState(0);
  const sincronizarStelOrderAhora = async () => {
    setSyncingStelOrder(true); setSyncResult(null);
    const authHeaders = { 'Content-Type': 'application/json', ...authService.authHeader() };
    const partes = [];
    let huboError = false;
    try {
      const subida = await fetch('/.netlify/functions/forzarSyncStelOrder', { method: 'POST', headers: authHeaders });
      const subidaData = await subida.json();
      if (subidaData.success) partes.push(`↑ ${subidaData.revisadas === 0 ? 'nada pendiente' : `${subidaData.conteo?.sincronizado || 0} subida(s)`}`);
      else { huboError = true; partes.push(`↑ error: ${subidaData.error || 'desconocido'}`); }
    } catch (e) {
      huboError = true; partes.push(`↑ error: ${e.message}`);
    }
    try {
      const bajada = await fetch('/.netlify/functions/forzarSyncDeltaStelOrder', { method: 'POST', headers: authHeaders });
      const bajadaData = await bajada.json();
      if (bajadaData.success) partes.push(`↓ ${bajadaData.totalOk || 0} actualizada(s)${bajadaData.totalFail ? `, ${bajadaData.totalFail} con error` : ''}`);
      else { huboError = true; partes.push(`↓ error: ${bajadaData.error || 'desconocido'}`); }
    } catch (e) {
      huboError = true; partes.push(`↓ error: ${e.message}`);
    }
    setSyncResult({ ok: !huboError, msg: partes.join(' · ') });
    setSyncingStelOrder(false);
    setRefreshTrigger(n => n + 1); // recarga la lista de órdenes con lo que acaba de bajar
    setTimeout(() => setSyncResult(null), 5000);
  };

  // Tema claro/oscuro: aplica al instante (updateUser dispara el efecto en
  // AuthContext que pone/quita la clase `dark`) y se guarda en segundo plano.
  const toggleTheme = async () => {
    const nuevoTema = (user?.theme || 'light') === 'dark' ? 'light' : 'dark';
    updateUser({ theme: nuevoTema });
    try {
      await fetch('/.netlify/functions/updateTheme', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ email: user.email, theme: nuevoTema }),
      });
    } catch (e) { console.error('[updateTheme]', e.message); }
  };

  // Registrar token FCM al montar la app (una vez por sesión de usuario)
  const registeredUid = React.useRef(null);
  useEffect(() => {
    if (user?.id && user.id !== registeredUid.current && 'Notification' in window && 'serviceWorker' in navigator) {
      registeredUid.current = user.id;
      registerPushToken(user);
    }
  }, [user]);

  useEffect(() => {
    const handleOnline  = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online',  handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online',  handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" style={{ maxWidth: 480, margin: '0 auto' }}>

      {/* Banner de estado: sin cobertura y/o envíos pendientes en la bandeja */}
      {(!isOnline || pending > 0 || failed > 0) && (
        <div className={`text-white text-center text-xs font-semibold py-2 px-4 sticky top-0 z-50 flex items-center justify-center gap-2 ${
          failed > 0 ? 'bg-red-600' : !isOnline ? 'bg-amber-500' : 'bg-blue-600'
        }`}>
          {!isOnline && <span>Sin conexión.</span>}
          {pending > 0 && (
            <span>{pending} cambio{pending !== 1 ? 's' : ''} sin enviar{isOnline ? ' — sincronizando…' : ' (se enviarán al reconectar)'}</span>
          )}
          {failed > 0 && <span>{failed} con error.</span>}
          {isOnline && (pending > 0 || failed > 0) && (
            <button onClick={flushNow} disabled={flushing}
              className="underline underline-offset-2 disabled:opacity-60">
              {flushing ? 'Reintentando…' : 'Reintentar ahora'}
            </button>
          )}
        </div>
      )}

      {/* Header */}
      <div className={`bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between ${(!isOnline || pending > 0 || failed > 0) ? 'sticky top-9' : 'sticky top-0'} z-40 shadow-sm`}>
        <div className="flex items-center gap-3">
          <img src={zapataLogo} alt="Zapata" className="h-8 w-8 object-contain" />
          <div>
            <p className="text-sm font-bold text-gray-900 leading-tight">{user?.name}</p>
            <p className="text-xs text-gray-400 leading-tight">Técnico</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={toggleTheme}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            title={(user?.theme || 'light') === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
          >
            {(user?.theme || 'light') === 'dark' ? <Icons.Sun className="w-5 h-5" /> : <Icons.Moon className="w-5 h-5" />}
          </button>
          {isAdminImpersonating && (
            <button
              onClick={sincronizarStelOrderAhora}
              disabled={syncingStelOrder}
              className="p-2 text-emerald-500 hover:text-emerald-700 transition-colors disabled:opacity-50"
              title="Sincronizar con StelOrder ahora — sube lo pendiente y baja lo nuevo (probando como técnico)"
            >
              <Icons.Refresh className={`w-5 h-5 ${syncingStelOrder ? 'animate-spin' : ''}`} />
            </button>
          )}
          {isAdminImpersonating && (
            <button
              onClick={exitTechnicianMode}
              className="p-2 text-blue-500 hover:text-blue-700 transition-colors"
              title="Cambiar a Director"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </button>
          )}
          <button
            onClick={() => { if (window.confirm('¿Cerrar sesión?')) logout(); }}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            title="Cerrar sesión"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
          </button>
        </div>
      </div>

      {/* Resultado de "Sincronizar con StelOrder ahora" — toast flotante y transitorio */}
      {syncResult && (
        <div className={`fixed top-16 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-xl shadow-lg text-white text-xs font-semibold ${syncResult.ok ? 'bg-emerald-600' : 'bg-red-600'}`}>
          {syncResult.ok ? '✓ ' : '⚠️ '}{syncResult.msg}
        </div>
      )}

      {/* Content area */}
      <div className="flex-1 overflow-y-auto pb-20">
        {activeTab === 'orders'      && <TechnicianOrdersView refreshTrigger={refreshTrigger} />}
        {activeTab === 'commissions' && <TechnicianCommissionsView />}
        {activeTab === 'contratos'   && <TechnicianContratosView />}
        {activeTab === 'profile'     && <TechnicianDashboardView />}
      </div>

      {/* Bottom navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40"
        style={{ maxWidth: 480, margin: '0 auto', left: '50%', transform: 'translateX(-50%)', width: '100%' }}>
        <div className="flex">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`relative flex-1 flex flex-col items-center gap-1 py-3 text-xs font-semibold transition-colors ${
                activeTab === tab.id ? 'text-red-600' : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              {activeTab === tab.id && (
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-red-600 rounded-b-full" />
              )}
              <span className="text-xl leading-none">{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
